import { Box, Chip, Paper, Typography, useMediaQuery } from "@mui/material";
import { administrationRequests } from "api/administrationApi";
import { employeeRequests } from "api/employeeApi";
import { studentRequests } from "api/studentApi";
import { settings } from "app";
import permissions from "app/permissions";
import CustomFilter from "features/CustomFilter";
import { MobileListRow } from "features/MobileListRow";
import React, { useCallback, useEffect, useState } from "react";
import {
  ArrayField,
  Datagrid,
  FunctionField,
  ListContextProvider,
  ReferenceArrayField,
  ReferenceField,
  ResourceContextProvider,
  SimpleList,
  SingleFieldList,
  TextField,
  Title,
  useNotify,
  useStore,
} from "react-admin";
import RoomField from "server/components/Fields/RoomField";
import SecuredComponent from "server/security/SecuredComponent";
import { formatDateOnlyBg } from "server/formatting/formatDateBg";
import dataProvider from "server/dataProvider";
import { WeekDaysChoices } from "utils/choices";
import {
  MOBILE_BREAKPOINT,
  StudentServicePath,
  StudyTypes,
  WEEKLY_SCHEDULE_NON_WORKING_OCCURRENCES,
  WeeklyScheduleStatusesTexts,
} from "utils/constants";
import { useNotifyError } from "utils/useNotifyError";
import RescheduleModal from "./components/RescheduleModal";

export const RESCHEDULE_OCCURRENCES_STORE_KEY = "rescheduleOccurrences.filters";

const HOLIDAY_BG_TEXT = "Празник";
const WEEKEND_BG_TEXT = "Уикенд";

const weekDayName = weekDay => WeekDaysChoices.find(d => d.id === weekDay)?.name;

const renderDate = record => `${formatDateOnlyBg(record.date)} (${weekDayName(record.weekDay)})`;

const renderReason = record =>
  [record.isWeekend && WEEKEND_BG_TEXT, record.isHoliday && HOLIDAY_BG_TEXT].filter(Boolean).join(" / ");

const renderTimeRange = record => `${record.startTime?.slice(0, 5)} - ${record.endTime?.slice(0, 5)}`;

const renderProfessorName = record => {
  const scientificDegree = record.scientificDegreeNameBg ?? "";
  return record.secondName
    ? `${scientificDegree} ${record.firstName} ${record.secondName} ${record.lastName}`
    : `${scientificDegree} ${record.firstName} ${record.lastName}`;
};

const renderReschedule = record =>
  record.reschedule ? (
    <span>
      {formatDateOnlyBg(record.reschedule.newDate)}
      {record.reschedule.startTime
        ? `, ${record.reschedule.startTime.slice(0, 5)} - ${record.reschedule.endTime.slice(0, 5)}`
        : ""}
      {record.reschedule.roomId ? (
        <>
          {", "}
          <RoomField source="reschedule.roomId" />
        </>
      ) : null}
    </span>
  ) : (
    "-"
  );

const DisciplineField = () => (
  <ReferenceField
    source="disciplineId"
    reference={administrationRequests.getDisciplinesSelect()}
    link={false}
    sortable={false}
  >
    <TextField source="name" />
  </ReferenceField>
);

const ClassTypeField = () => (
  <ReferenceField
    source="classTypeId"
    reference={administrationRequests.getRoomClassTypesSelect()}
    link={false}
    sortable={false}
  >
    <TextField source="name" />
  </ReferenceField>
);

const ProfessorsField = () => (
  <ReferenceArrayField
    source="professorEmployeeIds"
    reference={employeeRequests.getProfessorsSelect()}
    sortable={false}
  >
    <SingleFieldList linkType={false}>
      <FunctionField render={renderProfessorName} />
    </SingleFieldList>
  </ReferenceArrayField>
);

const ListRescheduleOccurrences = () => {
  const isMobile = useMediaQuery(MOBILE_BREAKPOINT);
  const notify = useNotify();
  const notifyError = useNotifyError();

  // filters drives the list and the gate (persisted via useStore); the fields below are the draft
  // selections bound to CustomFilter, committed on "Покажи".
  const [filters, setFilters] = useStore(RESCHEDULE_OCCURRENCES_STORE_KEY, null);
  const [academicYearId, setAcademicYearId] = useState(filters?.academicYearId ?? null);
  const [structuralUnitId, setStructuralUnitId] = useState(filters?.structuralUnitId ?? null);
  const [instructionLanguageId, setInstructionLanguageId] = useState(filters?.instructionLanguageId ?? null);
  const [studyTypeId, setStudyTypeId] = useState(filters?.studyTypeId ?? StudyTypes.Full_time);
  const [sessionId, setSessionId] = useState(filters?.academicYearSessionId ?? null);

  const [yearChoices, setYearChoices] = useState([]);
  const [structuralUnitChoices, setStructuralUnitChoices] = useState([]);
  const [instructionLanguageChoices, setInstructionLanguageChoices] = useState([]);
  const [studyTypeChoices, setStudyTypeChoices] = useState([]);
  const [sessionChoices, setSessionChoices] = useState([]);

  const [rows, setRows] = useState([]);
  const [sessionDateRange, setSessionDateRange] = useState(null);

  const academicYearSessionId = filters?.academicYearSessionId;

  // Static dropdown data — gates the whole filter, so a contextual message beats the raw error.
  // Single-option dropdowns are preselected, as the previous AutoPickSelectInput did.
  useEffect(() => {
    const loadChoices = async () => {
      try {
        const [years, units, languages, studyTypes] = await Promise.all([
          dataProvider.customRequest(studentRequests.getAcademicYearsSelect(), null, "GET"),
          dataProvider.customRequest(administrationRequests.getStructuralUnitsSelect(), null, "GET"),
          dataProvider.customRequest(administrationRequests.getInstructionLanguagesSelect(), null, "GET"),
          dataProvider.customRequest(administrationRequests.getStudyTypesSelect(), null, "GET"),
        ]);

        setYearChoices(years.data ?? []);
        setStructuralUnitChoices(units.data ?? []);
        setInstructionLanguageChoices(languages.data ?? []);
        setStudyTypeChoices(studyTypes.data ?? []);

        setAcademicYearId(prev => prev ?? years.data?.[0]?.id ?? null);
        if (units.data?.length === 1) setStructuralUnitId(prev => prev ?? units.data[0].id);
        if (languages.data?.length === 1) setInstructionLanguageId(prev => prev ?? languages.data[0].id);
      } catch (err) {
        notify("Неуспешно зареждане на филтрите. Опитайте да презаредите страницата.", { type: "error" });
      }
    };

    loadChoices();
  }, [notify]);

  // Semesters follow the draft year/unit/language/study type. CustomFilter labels options by
  // name, so map the session's sessionTypeText onto it.
  useEffect(() => {
    if (!(academicYearId && structuralUnitId && instructionLanguageId && studyTypeId)) {
      setSessionChoices([]);
      return;
    }

    dataProvider
      .customRequest(
        studentRequests.getAcademicSessionsSelect({
          onlySemesters: true,
          academicYearId,
          structuralUnitId,
          instructionLanguageId,
          studyTypeId,
        }),
        null,
        "GET",
      )
      .then(({ data }) => {
        const choices = (data ?? []).map(session => ({ id: session.id, name: session.sessionTypeText }));
        setSessionChoices(choices);
        if (choices.length === 1) setSessionId(prev => prev ?? choices[0].id);
      })
      .catch(err => notifyError(err));
  }, [academicYearId, structuralUnitId, instructionLanguageId, studyTypeId, notifyError]);

  const onSearch = () => {
    setFilters({
      academicYearId,
      structuralUnitId,
      instructionLanguageId,
      studyTypeId,
      academicYearSessionId: sessionId,
    });
  };

  const fetchOccurrences = useCallback(async () => {
    try {
      const { data } = await dataProvider.customRequest(
        studentRequests.getWeeklyScheduleNonWorkingOccurrences({ academicYearSessionId }),
        null,
        "GET",
      );
      setRows((data ?? []).map(occurrence => ({ ...occurrence, id: `${occurrence.groupId}_${occurrence.date}` })));
    } catch (err) {
      notifyError(err);
    }
  }, [academicYearSessionId, notifyError]);

  useEffect(() => {
    if (!academicYearSessionId) return;
    fetchOccurrences();
  }, [academicYearSessionId, fetchOccurrences]);

  useEffect(() => {
    if (!academicYearSessionId) return;

    // Background fetch — the modal degrades to server-side date validation without it
    dataProvider
      .customRequest(studentRequests.getAcademicSessionDates(academicYearSessionId), null, "GET")
      .then(({ data }) => setSessionDateRange(data))
      .catch(err => {
        console.error("Неуспешно зареждане на периода на семестъра", err);
        setSessionDateRange(null);
      });
  }, [academicYearSessionId]);

  const renderActions = record => (
    <SecuredComponent permission={permissions.canRescheduleWeeklySchedules}>
      <RescheduleModal
        record={record}
        sessionDateRange={sessionDateRange}
        onSuccess={fetchOccurrences}
      />
    </SecuredComponent>
  );

  return (
    <>
      <Title title="Пренасрочване на занятия" />
      <CustomFilter
        title="Избери семестър"
        cardWidth={isMobile ? "350px" : "700px"}
        buttonLabel="Покажи"
        buttonDisabled={!sessionId}
        buttonOnClick={onSearch}
        filters={[
          {
            label: "Академична година",
            type: "autocomplete",
            value: academicYearId,
            setValue: value => {
              setAcademicYearId(value);
              setStructuralUnitId(null);
              setInstructionLanguageId(null);
              setSessionId(null);
            },
            choices: yearChoices,
          },
          {
            label: "Структурна единица",
            type: "autocomplete",
            value: structuralUnitId,
            setValue: value => {
              setStructuralUnitId(value);
              setInstructionLanguageId(null);
              setSessionId(null);
            },
            choices: structuralUnitChoices,
            disable: !academicYearId,
          },
          {
            label: "Език на обучение",
            type: "autocomplete",
            value: instructionLanguageId,
            setValue: value => {
              setInstructionLanguageId(value);
              setSessionId(null);
            },
            choices: instructionLanguageChoices,
            disable: !structuralUnitId,
          },
          {
            label: "Вид на обучение",
            type: "autocomplete",
            value: studyTypeId,
            setValue: value => {
              setStudyTypeId(value);
              setSessionId(null);
            },
            choices: studyTypeChoices,
            disable: !instructionLanguageId,
          },
          {
            label: "Семестър",
            type: "autocomplete",
            value: sessionId,
            setValue: setSessionId,
            choices: sessionChoices,
            disable: !studyTypeId || !sessionChoices.length,
          },
        ]}
      />
      {!filters ? (
        <Paper sx={{ mt: 5 }}>
          <Box
            p={2}
            sx={{ display: "flex", justifyContent: "center" }}
          >
            <Typography
              align="center"
              color="secondary"
            >
              Изберете семестър, за да видите занятията в почивни дни.
            </Typography>
          </Box>
        </Paper>
      ) : (
        <ResourceContextProvider value={StudentServicePath + WEEKLY_SCHEDULE_NON_WORKING_OCCURRENCES}>
          <ListContextProvider value={{ data: rows }}>
            {isMobile ? (
              <SimpleList
                linkType={false}
                sx={settings.style.mobileList}
                primaryText={record => (
                  <div>
                    <MobileListRow fields={[{ label: "Дисциплина", value: <DisciplineField /> }]} />
                    <MobileListRow fields={[{ label: "Дата", value: renderDate(record) }]} />
                  </div>
                )}
                secondaryText={record => (
                  <div>
                    <MobileListRow fields={[{ label: "Причина", value: renderReason(record) }]} />
                    <MobileListRow fields={[{ label: "Седмица", value: record.weekNumber }]} />
                    <MobileListRow fields={[{ label: "Тип занятие", value: <ClassTypeField /> }]} />
                    <MobileListRow fields={[{ label: "Тип", value: record.type === 2 ? "В поток" : "Редовно" }]} />
                    <MobileListRow fields={[{ label: "Курс", value: record.courses?.join(", ") }]} />
                    <MobileListRow fields={[{ label: "Групи", value: record.studyGroups?.join(", ") }]} />
                    <MobileListRow fields={[{ label: "Час", value: renderTimeRange(record) }]} />
                    <MobileListRow fields={[{ label: "Зала", value: <RoomField /> }]} />
                    <MobileListRow fields={[{ label: "Преподаватели", value: <ProfessorsField /> }]} />
                    <MobileListRow
                      fields={[{ label: "Статус", value: WeeklyScheduleStatusesTexts[record.scheduleStatus] }]}
                    />
                    {record.reschedule && (
                      <MobileListRow fields={[{ label: "Пренасрочено за", value: renderReschedule(record) }]} />
                    )}
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>{renderActions(record)}</div>
                  </div>
                )}
              />
            ) : (
              <Datagrid
                rowClick={false}
                bulkActionButtons={false}
              >
                <FunctionField
                  label="Дата"
                  render={renderDate}
                />
                <FunctionField
                  label="Причина"
                  render={renderReason}
                />
                <TextField
                  source="weekNumber"
                  label="Седмица"
                  sortable={false}
                />
                <DisciplineField label="Дисциплина" />
                <TextField
                  source="curriculumModuleName"
                  label="Модул"
                  emptyText="-"
                  sortable={false}
                />
                <ClassTypeField label="Тип занятие" />
                <FunctionField
                  label="Тип"
                  render={record => (record.type === 2 ? "В поток" : "Редовно")}
                />
                <FunctionField
                  label="Курс"
                  render={record => record.courses?.join(", ") ?? "-"}
                />
                <ArrayField
                  source="studyGroups"
                  label="Групи"
                  sortable={false}
                >
                  <SingleFieldList linkType={false}>
                    <FunctionField
                      render={rec => (
                        <Chip
                          key={rec}
                          label={rec}
                        />
                      )}
                    />
                  </SingleFieldList>
                </ArrayField>
                <FunctionField
                  label="Час"
                  render={renderTimeRange}
                />
                <RoomField
                  source="roomId"
                  label="Зала"
                  sortable={false}
                />
                <ProfessorsField label="Преподаватели" />
                <FunctionField
                  label="Статус"
                  render={record => WeeklyScheduleStatusesTexts[record.scheduleStatus]}
                />
                <FunctionField
                  label="Пренасрочено за"
                  render={renderReschedule}
                />
                <FunctionField render={renderActions} />
              </Datagrid>
            )}
          </ListContextProvider>
        </ResourceContextProvider>
      )}
    </>
  );
};

export default ListRescheduleOccurrences;
